import { useState, useMemo, useEffect, useCallback } from "react";
import { ChevronUp, ChevronDown, Eye, UserPlus } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "./ButtonComponent";
import AssignPatientModal from "./AssignPatientModal";
import { db } from "../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  limit,
} from "firebase/firestore";
import { PAGINATION, SORT_OPTIONS, FILTER_OPTIONS } from "../constants/app";
import { patientUtils, vitalUtils, storage, arrayUtils } from "../utils";
import type { Patient, SortState, PaginationState } from "../types";

interface DataTableProps {
  data: Patient[];
  searchQuery?: string;
  riskLevelFilter?: string;
  ageGroupFilter?: string;
  genderFilter?: string;
  userEmail?: string;
  isAdmin?: boolean;
  userId?: string;
  onPatientListRefresh?: () => void;
}

type SortField = "name" | "age" | "lastSync" | "bloodPressure" | "riskLevel";

export default function DataTable({
  data,
  searchQuery = "",
  riskLevelFilter = FILTER_OPTIONS.ALL,
  ageGroupFilter = FILTER_OPTIONS.ALL,
  genderFilter = FILTER_OPTIONS.ALL,
  isAdmin = false,
  onPatientListRefresh,
}: DataTableProps) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for assignment modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<{ id: string; name: string; hospitalId?: string } | null>(null);

  // Use structured state for better organization
  const [sortState, setSortState] = useState<SortState>({
    field: SORT_OPTIONS.FIELDS.RISK_LEVEL,
    direction: SORT_OPTIONS.DIRECTIONS.DESC,
  });

  const [paginationState, setPaginationState] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    recordsPerPage: PAGINATION.RECORDS_PER_PAGE,
    totalRecords: 0,
  });

  const [unreadMessages, setUnreadMessages] = useState<{
    [userId: string]: boolean;
  }>({});

  // Helper to get last read timestamp for a patient from localStorage
  const getLastReadTimestamp = useCallback((userId: string): number => {
    try {
      const readPatients = JSON.parse(storage.get("readPatients") || "{}");
      return readPatients[userId] || 0;
    } catch {
      return 0;
    }
  }, []);

  // Listen for new messages from patients
  useEffect(() => {
    const unsubscribes = data.map((patient) => {
      const chatId = `${patient.userId}_dr_gupta`;
      const q = query(
        collection(db, "chats", chatId, "messages"),
        orderBy("createdAt", "desc"),
        limit(1)
      );
      return onSnapshot(q, (snapshot) => {
        const latest = snapshot.docs[0]?.data();
        const latestTimestamp = latest?.createdAt?.seconds || 0;
        const lastRead = getLastReadTimestamp(patient.userId);
        if (latest && latest.from === "patient" && latestTimestamp > lastRead) {
          setUnreadMessages((prev) => ({ ...prev, [patient.userId]: true }));
        } else {
          setUnreadMessages((prev) => ({ ...prev, [patient.userId]: false }));
        }
      });
    });
    return () => unsubscribes.forEach((unsub) => unsub && unsub());
  }, [data]);

  // Clear unread and update last read timestamp in localStorage when navigating to patient details
  const handleNavigate = useCallback(
    (patientId: string) => {
      setUnreadMessages((prev) => ({ ...prev, [patientId]: false }));
      // Mark as read in localStorage with current timestamp
      try {
        const readPatients = JSON.parse(storage.get("readPatients") || "{}");
        readPatients[patientId] = Math.floor(Date.now() / 1000); // store as seconds
        storage.set("readPatients", JSON.stringify(readPatients));
        // Store which patient we're navigating to
        storage.set("visitedPatientId", patientId);
        navigate(`/patient/${patientId}`);
      } catch (error) {
        // Fallback to direct navigation if localStorage fails
        navigate(`/patient/${patientId}`);
      }
    },
    [navigate]
  );

  // Clear unread messages when navigating to home page
  useEffect(() => {
    if (location.pathname === "/") {
      // Get the patient ID that was just visited
      const visitedPatientId = storage.get("visitedPatientId");
      if (visitedPatientId) {
        // Only clear the unread message for the visited patient
        setUnreadMessages((prev) => {
          const updated = { ...prev };
          updated[visitedPatientId] = false;
          return updated;
        });
        storage.remove("visitedPatientId");
      }
    }
  }, [location.pathname]);

  // Filter and search logic using utility functions
  const filteredData = useMemo(() => {
    return data.filter((patient) => {
      const fullName = patientUtils.getFullName(patient);
      const gender = patientUtils.getGender(patient);
      const riskLevel = patientUtils.getRiskLevel(patient);
      const age = patientUtils.getAge(patient);
      const dob = patient.personal_information?.dob || "";

      // Skip patients with empty names
      if (!fullName || fullName.trim() === "") {
        return false;
      }

      // Search filter - only on Patient name, dob, and risk level
      const matchesSearch =
        searchQuery === "" ||
        fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dob.includes(searchQuery) ||
        riskLevel.toLowerCase().includes(searchQuery.toLowerCase());

      // Risk level filter
      const matchesRiskLevel =
        riskLevelFilter === FILTER_OPTIONS.ALL ||
        riskLevel.toLowerCase() === riskLevelFilter.toLowerCase();

      // Gender filter
      const matchesGender =
        genderFilter === FILTER_OPTIONS.ALL ||
        gender.toLowerCase() === genderFilter.toLowerCase();

      // Age group filter using utility function
      const matchesAgeGroup =
        ageGroupFilter === FILTER_OPTIONS.ALL ||
        (() => {
          switch (ageGroupFilter) {
            case "18-35":
              return age >= 18 && age <= 35;
            case "36-50":
              return age >= 36 && age <= 50;
            case "51-65":
              return age >= 51 && age <= 65;
            case "65+":
              return age > 65;
            default:
              return true;
          }
        })();

      return (
        matchesSearch && matchesRiskLevel && matchesAgeGroup && matchesGender
      );
    });
  }, [data, searchQuery, riskLevelFilter, ageGroupFilter, genderFilter]);

  // Optimized sort logic using utility functions
  const sortedData = useMemo(() => {
    return arrayUtils.sortBy(
      filteredData,
      (patient) => {
        switch (sortState.field) {
          case SORT_OPTIONS.FIELDS.NAME:
            return patientUtils.getFullName(patient).toLowerCase();
          case SORT_OPTIONS.FIELDS.AGE:
            return patientUtils.getAge(patient);
          case SORT_OPTIONS.FIELDS.LAST_SYNC:
            return patient.score_summary?.tested_at || "";
          case SORT_OPTIONS.FIELDS.RISK_LEVEL:
            const riskOrder = { good: 1, average: 2, poor: 3 };
            const riskLevel = patientUtils.getRiskLevel(patient).toLowerCase();
            return riskOrder[riskLevel as keyof typeof riskOrder] || 0;
          default:
            return patientUtils.getRiskLevel(patient);
        }
      },
      sortState.direction
    );
  }, [filteredData, sortState]);

  // Optimized pagination logic
  const paginatedData = useMemo(() => {
    const totalPages = Math.ceil(
      sortedData.length / paginationState.recordsPerPage
    );
    const startIndex =
      (paginationState.currentPage - 1) * paginationState.recordsPerPage;
    const endIndex = startIndex + paginationState.recordsPerPage;

    // Update pagination state if needed
    if (
      paginationState.totalPages !== totalPages ||
      paginationState.totalRecords !== sortedData.length
    ) {
      setPaginationState((prev) => ({
        ...prev,
        totalPages,
        totalRecords: sortedData.length,
      }));
    }

    return sortedData.slice(startIndex, endIndex);
  }, [
    sortedData,
    paginationState.currentPage,
    paginationState.recordsPerPage,
    paginationState.totalPages,
    paginationState.totalRecords,
  ]);

  // Memoized handlers for better performance
  const handleSort = useCallback((field: string) => {
    setSortState((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === SORT_OPTIONS.DIRECTIONS.ASC
          ? SORT_OPTIONS.DIRECTIONS.DESC
          : SORT_OPTIONS.DIRECTIONS.ASC,
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setPaginationState((prev) => ({ ...prev, currentPage: page }));
  }, []);

  // Reset to first page when data changes
  useEffect(() => {
    setPaginationState((prev) => ({ ...prev, currentPage: 1 }));
  }, [searchQuery, riskLevelFilter, ageGroupFilter, genderFilter, data]);

  // Map risk level to badge color and text
  const getRiskLevelBadge = useCallback((riskLevel: string) => {
    const normalized = riskLevel.toLowerCase();
    let colorClass = "";
    let displayText = "";

    if (normalized === "good") {
      colorClass =
        "bg-rag-green text-white border-none font-normal hover:bg-rag-green";
      displayText = "Good";
    } else if (normalized === "average") {
      colorClass =
        "bg-rag-amber text-white border-none font-normal hover:bg-rag-amber";
      displayText = "Average";
    } else if (normalized === "poor") {
      colorClass =
        "bg-rag-red text-white border-none font-normal hover:bg-rag-red";
      displayText = "Poor";
    } else {
      // Fallback for unknown values
      colorClass =
        "bg-gray-500 text-white border-none font-normal hover:bg-gray-500";
      displayText =
        riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1).toLowerCase();
    }

    return { colorClass, displayText };
  }, []);

  const getHeartRateColor = useCallback((heartRate: number) => {
    return vitalUtils
      .getHeartRateColor(heartRate)
      .replace("text-red-500", "text-rag-red")
      .replace("text-green-500", "text-rag-green");
  }, []);

  const getBloodPressureColor = useCallback(
    (systolic: number, diastolic: number) => {
      return vitalUtils
        .getBloodPressureColor(systolic, diastolic)
        .replace("text-red-500", "text-rag-red")
        .replace("text-yellow-500", "text-rag-amber")
        .replace("text-green-500", "text-rag-green");
    },
    []
  );

  const getBloodOxygenColor = useCallback((bloodOxygen: number) => {
    return vitalUtils
      .getBloodOxygenColor(bloodOxygen)
      .replace("text-red-500", "text-rag-red")
      .replace("text-yellow-500", "text-rag-amber")
      .replace("text-green-500", "text-rag-green");
  }, []);

  const formatLastSync = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const SortIcon = useCallback(
    ({ field }: { field: SortField }) => {
      if (sortState.field !== field)
        return <ChevronUp className="h-4 w-4 text-gray-400" />;
      return sortState.direction === SORT_OPTIONS.DIRECTIONS.ASC ? (
        <ChevronUp className="h-4 w-4 text-gray-600" />
      ) : (
        <ChevronDown className="h-4 w-4 text-gray-600" />
      );
    },
    [sortState]
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 h-[calc(100vh-200px)] flex flex-col">
      <div className="overflow-auto rounded-2xl flex-1">
        <table className="w-full">
          <thead className="bg-gray-50 rounded-2xl sticky top-0 z-10">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center space-x-1">
                  <span>Patient Name</span>
                  <SortIcon field="name" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("age")}
              >
                <div className="flex items-center space-x-1">
                  <span>Age</span>
                  <SortIcon field="age" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100">
                <div className="flex items-center space-x-1">
                  <span>Gender</span>
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("riskLevel")}
              >
                <div className="flex items-center space-x-1">
                  <span>Heart Health</span>
                  <SortIcon field="riskLevel" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100">
                <div className="flex items-center space-x-1">
                  <span>Heart Rate (bpm)</span>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100">
                <div className="flex items-center space-x-1">
                  <span>Blood Pressure (mmHg)</span>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100">
                <div className="flex items-center space-x-1">
                  <span>Blood Oxygen</span>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100">
                <div className="flex items-center space-x-1">
                  <span>Sleep</span>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100">
                <div className="flex items-center space-x-1">
                  <span>Steps</span>
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("lastSync")}
              >
                <div className="flex items-center space-x-1">
                  <span>Last Sync</span>
                  <SortIcon field="lastSync" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-6 py-12 text-center">
                  <p className="text-gray-500">
                    {data.length === 0
                      ? "No patients found"
                      : "No patients found matching your criteria"}
                  </p>
                </td>
              </tr>
            ) : (
              paginatedData.map((patient) => {
                const fullName = patient.personal_information?.full_name || "-";
                const gender = patient.personal_information?.gender || "-";
                const riskLevel = patient.score_summary?.risk_level || "-";

                // Helper to get latest vital by bodyType
                function getLatestVital(bodyType: string) {
                  if (!patient.vitals) return undefined;
                  let all: any[] = [];
                  // patient.vitals is an object with moduleId keys, each value is an array
                  Object.values(patient.vitals).forEach((arr: any) => {
                    if (Array.isArray(arr)) {
                      all = all.concat(
                        arr.filter((v: any) => v.bodyType === bodyType)
                      );
                    }
                  });
                  if (all.length === 0) return undefined;
                  all.sort(
                    (a: any, b: any) =>
                      new Date(b.created_at).getTime() -
                      new Date(a.created_at).getTime()
                  );
                  return all[0];
                }

                // Extract vitals or show "-" when not available
                const bpVital = getLatestVital("BLOOD_PRESSURE");
                const hrVital = getLatestVital("HEART_RATE");
                const boVital = getLatestVital("BLOOD_OXYGEN");
                const stepsVital = getLatestVital("STEPS");

                // Blood Pressure
                let bloodPressure = "-";
                if (
                  bpVital &&
                  bpVital.body &&
                  bpVital.body.systolic &&
                  bpVital.body.diastolic
                ) {
                  bloodPressure = `${bpVital.body.systolic}/${bpVital.body.diastolic}`;
                }

                // Heart Rate
                const heartRate =
                  hrVital && hrVital.body && hrVital.body.value
                    ? hrVital.body.value
                    : "-";

                // Blood Oxygen
                const bloodOxygen =
                  boVital && boVital.body && boVital.body.value
                    ? boVital.body.value
                    : "-";

                // Steps
                const steps =
                  stepsVital && stepsVital.body && stepsVital.body.value
                    ? stepsVital.body.value
                    : "-";

                // Sleep
                const sleep = patient.score_summary?.inputSummary?.sleep || "-";
                // Use utility function for age calculation
                const age = patientUtils.getAge(patient) || "-";
                // Last Sync
                const lastSync = patient.score_summary?.tested_at;
                // Use patient.userId for navigation
                const patientId = patient.userId;
                // Use helpers for color and formatting
                return (
                  <tr
                    key={patientId}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleNavigate(patientId)}
                  >
                    <td className="px-6 py-2 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {fullName}
                      </div>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                      {age}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                      {gender.charAt(0).toUpperCase() +
                        gender.slice(1).toLowerCase()}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap">
                      {(() => {
                        const badge = getRiskLevelBadge(riskLevel);
                        return (
                          <Button
                            className={`w-[66px] !py-0.5 text-sm !rounded-lg ${badge.colorClass}`}
                          >
                            {badge.displayText}
                          </Button>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium">
                      {heartRate === "-" ? (
                        <span className="text-gray-500">{heartRate}</span>
                      ) : (
                        <span className={getHeartRateColor(heartRate)}>
                          {heartRate}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium">
                      {bloodPressure === "-" ? (
                        <span className="text-gray-500">{bloodPressure}</span>
                      ) : (
                        <span
                          className={getBloodPressureColor(
                            bpVital?.body?.systolic || 0,
                            bpVital?.body?.diastolic || 0
                          )}
                        >
                          {bloodPressure}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium">
                      {bloodOxygen === "-" ? (
                        <span className="text-gray-500">{bloodOxygen}</span>
                      ) : (
                        <span className={getBloodOxygenColor(bloodOxygen)}>
                          {bloodOxygen}%
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                      {sleep}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                      {steps === "-" ? steps : steps.toLocaleString()}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                      {formatLastSync(lastSync)}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex gap-2">
                        <button
                          className="relative bg-gray-100 hover:bg-gray-200 rounded-xl p-3 flex items-center justify-center focus:outline-none shadow-none border-none transition"
                          title="View"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigate(patientId);
                          }}
                        >
                          <Eye
                            className="w-5 h-5"
                            style={{ width: 20, height: 20 }}
                          />
                          {unreadMessages[patientId] && (
                            <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-500"></span>
                          )}
                        </button>
                        {isAdmin && (
                          <button
                            className="bg-blue-100 hover:bg-blue-200 rounded-xl p-3 flex items-center justify-center focus:outline-none shadow-none border-none transition"
                            title="Assign To Hospital"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPatient({
                                id: patientId,
                                name: fullName,
                                hospitalId: patient.hospital_id,
                              });
                              setAssignModalOpen(true);
                            }}
                          >
                            <UserPlus
                              className="w-5 h-5 text-blue-600"
                              style={{ width: 20, height: 20 }}
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {sortedData.length > 0 && (
        <div className="rounded-2xl bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-between">
          {/* Showing X to Y of Z patients */}
          <div className="flex items-center">
            <p className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">
                {Math.min(
                  (paginationState.currentPage - 1) *
                    paginationState.recordsPerPage +
                    1,
                  sortedData.length
                )}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(
                  paginationState.currentPage * paginationState.recordsPerPage,
                  sortedData.length
                )}
              </span>{" "}
              of <span className="font-medium">{sortedData.length}</span>{" "}
              patients
            </p>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center space-x-2">
            {/* First Page Button */}
            <button
              onClick={() => handlePageChange(1)}
              disabled={
                paginationState.currentPage === 1 || sortedData.length === 0
              }
              className={`p-2 rounded-md text-sm ${
                paginationState.currentPage === 1 || sortedData.length === 0
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-blue-600 hover:text-blue-700"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Previous Button */}
            <button
              onClick={() =>
                handlePageChange(Math.max(paginationState.currentPage - 1, 1))
              }
              disabled={
                paginationState.currentPage === 1 || sortedData.length === 0
              }
              className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium ${
                paginationState.currentPage === 1 || sortedData.length === 0
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-blue-600 hover:text-blue-700"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span>Previous</span>
            </button>

            {/* Current Page Input */}
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={paginationState.currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= paginationState.totalPages) {
                    handlePageChange(page);
                  }
                }}
                className="w-12 px-2 py-1 text-center text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
                max={paginationState.totalPages}
              />
              <span className="text-sm text-gray-600">
                of {paginationState.totalPages}
              </span>
            </div>

            {/* Next Button */}
            <button
              onClick={() =>
                handlePageChange(
                  Math.min(
                    paginationState.currentPage + 1,
                    paginationState.totalPages
                  )
                )
              }
              disabled={
                paginationState.currentPage === paginationState.totalPages ||
                sortedData.length === 0
              }
              className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium ${
                paginationState.currentPage === paginationState.totalPages ||
                sortedData.length === 0
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-blue-600 hover:text-blue-700"
              }`}
            >
              <span>Next</span>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Last Page Button */}
            <button
              onClick={() => handlePageChange(paginationState.totalPages)}
              disabled={
                paginationState.currentPage === paginationState.totalPages ||
                sortedData.length === 0
              }
              className={`p-2 rounded-md text-sm ${
                paginationState.currentPage === paginationState.totalPages ||
                sortedData.length === 0
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-blue-600 hover:text-blue-700"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Assign Patient Modal */}
      {selectedPatient && (
        <AssignPatientModal
          isOpen={assignModalOpen}
          onClose={() => {
            setAssignModalOpen(false);
            setSelectedPatient(null);
          }}
          patientId={selectedPatient.id}
          patientName={selectedPatient.name}
          currentHospitalId={selectedPatient.hospitalId}
          onAssignmentSuccess={() => {
            if (onPatientListRefresh) {
              onPatientListRefresh();
            }
          }}
        />
      )}
    </div>
  );
}
